/**
 * Time Clock v2 context (rebuilt Oct 29 2026): the shared clock's session.
 *
 * The device is signed in to the RESTAURANT (the Time Clock app: lib/pwaAccount; /time-clock:
 * the back office's session). Employees identify themselves on it (name or phone + PIN) and
 * punch THE time clock (labor-service /api/timeclock/v2 -> time_clock_entries, the rows
 * Payroll pays). Offline, punches are kept on the device (lib/timeclock/punchQueue) and sent
 * when it is back online.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { timeClockApi } from '@/lib/timeclock/api';
import { pendingCount, startAutoSync, syncPunches, QUEUE_CHANGED } from '@/lib/timeclock/punchQueue';

const TimeclockContext = createContext(null);
const ROSTER_KEY = (rid) => `chain29_timeclock_roster_${rid}`;
const ME_KEY = 'chain29_timeclock_me';

function readJson(key) {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
}

function writeJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage full / blocked */ }
}

export function TimeclockProvider({ authHeaders, auth = 'account', children }) {
  const api = useMemo(() => timeClockApi(authHeaders), [authHeaders]);
  const [me, setMe] = useState(() => readJson(ME_KEY));
  const [roster, setRoster] = useState([]);
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const refreshPending = useCallback(async () => setPending(await pendingCount()), []);

  const load = useCallback(async () => {
    try {
      const info = await api.me();
      setMe(info);
      writeJson(ME_KEY, info);
      setLoadError(null);
      const list = await api.roster();
      setRoster(list.employees || []);
      writeJson(ROSTER_KEY(info.restaurant.id), list.employees || []);
    } catch (e) {
      if (e.network) {
        const cached = readJson(ME_KEY);
        if (cached) {
          setMe(cached);
          setRoster(readJson(ROSTER_KEY(cached.restaurant.id)) || []);
        }
      } else {
        setLoadError(e.message);
      }
    }
  }, [api]);

  const syncNow = useCallback(async () => {
    if (!navigator.onLine) return null;
    setSyncing(true);
    try {
      const result = await syncPunches();
      setLastSync({ at: new Date(), ...result });
      return result;
    } finally {
      setSyncing(false);
      refreshPending();
    }
  }, [refreshPending]);

  useEffect(() => {
    load();
    startAutoSync();
    refreshPending();
    const up = () => { setOnline(true); load(); };
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    window.addEventListener(QUEUE_CHANGED, refreshPending);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
      window.removeEventListener(QUEUE_CHANGED, refreshPending);
    };
  }, [load, refreshPending]);

  const value = {
    api, auth, me, roster, online, pending, syncing, lastSync, loadError,
    timezone: me?.restaurant?.timezone,
    restaurantId: me?.restaurant?.id,
    reload: load, syncNow, refreshPending,
  };
  return <TimeclockContext.Provider value={value}>{children}</TimeclockContext.Provider>;
}

export function useTimeclock() {
  const ctx = useContext(TimeclockContext);
  if (!ctx) throw new Error('useTimeclock must be used within a TimeclockProvider');
  return ctx;
}

export default TimeclockContext;
